import { useState } from "react";
import { Alert, styled } from "@monolith-forensics/monolith-ui";
import type { AlertVariant } from "@monolith-forensics/monolith-ui";
import { useAuth } from "@/contexts/AuthContext";

const StyledBanner = styled.div`
  .dismissable-banner-actions {
    display: inline-flex;
    flex-wrap: wrap;
    gap: 12px;
    margin-left: 12px;
  }
  .dismissable-banner-actions a {
    color: ${({ theme }) => theme.palette.primary.main};
  }
`;

export const dismissedBannerKey = (id: string, userId: string | number) =>
  `MONOLITH.DISMISSED_BANNER.${userId}.${id}`;

// Local storage can throw (blocked storage, private windows); the banner
// shows in that case rather than failing to render.
const readDismissed = (key: string) => {
  try {
    return window.localStorage.getItem(key) === "true";
  } catch {
    return false;
  }
};

const writeDismissed = (key: string) => {
  try {
    window.localStorage.setItem(key, "true");
  } catch {
    // Dismissed for this visit only.
  }
};

/**
 * A page level info banner the user can close, remembered per user in this
 * browser. Bump the version in `id` (e.g. `evidence-files-tab:v2`) to show
 * reworded copy again to users who dismissed the old one. Works in dialogs
 * too, for news about the dialog itself (the Print Labels dialog, ENG-2241).
 * Alerts about what the dialog's action will do (the Evidence Files modal
 * alerts) use the monolith-ui `Alert` directly and aren't dismissable.
 */
export const DismissableBanner: React.FC<{
  id: string;
  title?: string;
  variant?: AlertVariant;
  // Links shown at the end of the body's line (Maher, 2026-10-19).
  actions?: React.ReactNode;
  children: React.ReactNode;
  className?: string;
  "data-testid"?: string;
}> = ({
  id,
  className,
  title,
  variant = "info",
  actions,
  children,
  "data-testid": testId,
}) => {
  const { currentUser } = useAuth();
  const key = dismissedBannerKey(id, currentUser.user_id);
  const [dismissed, setDismissed] = useState(() => readDismissed(key));

  if (dismissed) return null;

  return (
    <StyledBanner className={className} data-testid={testId}>
      <Alert
        variant={variant}
        title={title}
        showCloseButton
        onClose={() => {
          writeDismissed(key);
          setDismissed(true);
        }}
      >
        <div>
          {children}
          {actions && (
            <span className="dismissable-banner-actions">{actions}</span>
          )}
        </div>
      </Alert>
    </StyledBanner>
  );
};

export default DismissableBanner;
